<template>
  <BaseScreen v-bind="screen">
    <template #actions>
      <BaseButton
        v-for="(item, i) in actions"
        :key="`action_${i}`"
        :text="item.text"
        :is-outlined="item.isOutlined"
        size="s"
        @click="item.action"
      />
    </template>
    <div
      class="screen__timer"
      :style="{ visibility: isRecording ? 'visible' : 'hidden' }"
      slot="title"
    >
      <BaseStopwatch
        ref="timer"
        :has-indicator="isRecording"
      />
    </div>
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Camera
      ref="camera"
      class="screen__camera"
      @start="onCameraStart"
      @error="showError"
      @video="onVideoReady"
    >
      <template v-if="isVideoReady" #overlay>
        <ModalConfirm
          v-bind="watchModal"
          class="camera__overlay"
          is-clean
          @agree="playVideo"
          @disagree="goNext"
        />
      </template>
    </Camera>
    <ModalConfirm
      v-if="isAbortModalVisible"
      v-bind="abortModal"
      slot="root"
      class="screen__overlay"
      @agree="abortRecording"
      @disagree="hideAbortModal"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import BaseButton from '@/components/BaseButton.vue';
import BaseStopwatch from '@/components/BaseStopwatch.vue';
import ModalConfirm from '@/components/ModalConfirm.vue';
import Camera from '@/components/Camera.vue';

import CONSTANTS from '@/helpers/constants';

const {
  STEP_VIDEO_RECORD_IS_READY,
  STEP_MODAL_CONFIRM_VIDEO_ABORT,
  ACTION_REMAKE,
  ACTION_PLAY,
  ACTION_CONTINUE,
} = CONSTANTS;

export default {
  name: 'TakeVideoScreen',
  mixins: [screenMixin],
  components: {
    BaseButton,
    BaseStopwatch,
    ModalConfirm,
    Camera,
  },
  data() {
    return {
      isRecording: false,
      isAbortModalVisible: false,
      isVideoProcessing: false,
      isCameraStarted: false,
    };
  },
  staticData: () => ({
    abortPath: '',
  }),
  computed: {
    ...mapState('feedback', [
      'video',
    ]),
    title() {
      return this.isVideoReady ? this.$t('titles.recordIsFinish') : '';
    },
    pendingText() {
      return this.isVideoProcessing
        ? this.$t('pending.processing')
        : this.$t('pending.hardware');
    },
    actions() {
      if (this.isRecording) {
        return [
          {
            text: this.$t('actions.abort'),
            action: () => this.confirmAbort(this.$router.options.paths.home),
            isOutlined: true,
          },
          {
            text: this.$t('actions.finish'),
            action: this.stopRecording,
          },
        ];
      }
      if (this.isVideoReady) {
        return [
          {
            text: this.$t('actions.rewrite'),
            action: this.startNewRecording,
            isOutlined: true,
          },
        ];
      }
      return [
        {
          text: this.$t('actions.start'),
          action: this.startRecording,
        },
      ];
    },
    error() {
      return {
        title: this.$t('titles.badHardware'),
        text: this.$t('texts.videoNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
          {
            text: this.$t('actions.skipVideo'),
            action: () => this.$router.push(this.$router.options.paths.sendVideo),
          },
        ],
      };
    },
    abortModal() {
      return {
        title: this.$t('titles.confirmAbortRecording'),
        agreeText: this.$t('actions.yesAbort'),
        disagreeText: this.$t('actions.noContinue'),
        replic: STEP_MODAL_CONFIRM_VIDEO_ABORT,
      };
    },
    watchModal() {
      return {
        title: this.$t('titles.confirmVideoWatching'),
        agreeText: this.$t('actions.view'),
        disagreeText: this.$t('actions.continue'),
      };
    },
    isVideoReady() {
      return Boolean(this.video);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isRecording) {
      this.confirmAbort(to.path);
    } else {
      next();
    }
  },
  created() {
    this.showPending(0);
    this.initActions();
  },
  mounted() {
    this.startCamera();
  },
  methods: {
    ...mapMutations('feedback', [
      'setVideo',
      'clearVideo',
    ]),
    onCameraStart() {
      this.isCameraStarted = true;
      this.startNewRecording();
      this.hidePending();
    },
    onVideoReady(src) {
      this.setVideo(src);
      this.hideVideoProcessing();
      this.sayVideoReady();
    },
    sayVideoReady() {
      this.$robot.dialogService.setCase(STEP_VIDEO_RECORD_IS_READY, true, true);
      this.$robot.dialogService.sayReplicByName(STEP_VIDEO_RECORD_IS_READY, true);
    },
    confirmAbort(path) {
      this.abortPath = path || this.$router.options.paths.home;
      this.showAbortModal();
    },
    abortRecording() {
      return this.stopRecording()
        .then(() => {
          this.$router.push(this.abortPath);
        });
    },
    playVideo() {
      if (!this.isVideoReady) return;
      const url = this.video.fullPath.split('/home/promobot/Videos/').pop();
      this.$robot.dialogService.sayReplicByData({ text: '', url }, true);
    },
    startCamera() {
      return this.$refs.camera?.start();
    },
    startRecording() {
      return this.$refs.camera?.startRecording().then(this.onStartRecording);
    },
    stopRecording() {
      this.showVideoProcessing();
      return this.$refs.camera?.stopRecording().finally(this.onStopRecording);
    },
    onStartRecording() {
      this.isRecording = true;
      this.startTimer();
    },
    onStopRecording() {
      this.isRecording = false;
      this.stopTimer();
    },
    startNewRecording() {
      if (this.isRecording || !this.isCameraStarted) return;
      this.startRecording().then(this.clearVideo);
    },
    showVideoProcessing() {
      this.isVideoProcessing = true;
      this.showPending();
    },
    hideVideoProcessing() {
      this.isVideoProcessing = false;
      this.hidePending();
    },
    startTimer() {
      return this.$refs.timer?.start();
    },
    stopTimer() {
      return this.$refs.timer?.stop();
    },
    showAbortModal() {
      this.isAbortModalVisible = true;
    },
    hideAbortModal() {
      this.isAbortModalVisible = false;
    },
    goNext() {
      if (!this.isVideoReady) return;
      this.$router.push(this.$router.options.paths.sendVideo);
    },
    initActions() {
      const rootActions = [
        [ACTION_REMAKE, this.startNewRecording],
        [ACTION_PLAY, this.playVideo],
        [ACTION_CONTINUE, this.goNext],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
